import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { api } from '../../services/api';
import { Link } from 'react-router-dom';

interface CustodialExchange {
  exchangeId: number;
  extId: string;
  type: string;
  userId: number;
  fromAssetId: number;
  toAssetId: number;
  fromAmount: string;
  toAmount: string | null;
  rate: string | null;
  status: string;
  blockchainName: string | null;
  createdAt: string;
  updatedAt: string;
  extIdImex: string | null;
  fromAssetCode: string;
  fromAssetName: string;
  toAssetCode: string;
  toAssetName: string;
}

const CustodyExchangesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exchanges, setExchanges] = useState<CustodialExchange[]>([]);

  useEffect(() => {
    fetchExchanges();
  }, []);

  const fetchExchanges = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/admin-panel/exchanges/custody', {
        headers: { 'x-hasura-admin-secret': 'admin_secret_key' }
      });

      // Handle the double data nesting
      if (response.data.success && response.data.data.success) {
        setExchanges(response.data.data.data.exchanges);
      }
    } catch (err: any) {
      console.error('Error fetching exchanges:', err);
      setError(err.response?.data?.message || err.message || 'Error loading exchanges');
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip label="Завершен" color="success" size="small" />;
      case 'created':
        return <Chip label="Создан" color="info" size="small" />;
      case 'processing':
        return <Chip label="В обработке" color="warning" size="small" />;
      case 'failed':
        return <Chip label="Неуспешен" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Из</TableCell>
              <TableCell>В</TableCell>
              <TableCell>Сумма</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Дата создания</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exchanges.map((exchange) => (
              <TableRow key={exchange.exchangeId}>
                <TableCell>{exchange.exchangeId}</TableCell>
                <TableCell>
                  {exchange.fromAssetName} ({exchange.fromAssetCode})
                </TableCell>
                <TableCell>
                  {exchange.toAssetName} ({exchange.toAssetCode})
                </TableCell>
                <TableCell>{exchange.fromAmount}</TableCell>
                <TableCell>{renderStatus(exchange.status)}</TableCell>
                <TableCell>
                  {format(new Date(exchange.createdAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustodyExchangesPage;
